/* =============================================================================
   Safegear compare - Horizon 4.1.3
   -----------------------------------------------------------------------------
   Loaded by sections/sg-compare.liquid on /pages/compare only.

   Reads ?items=handle-a,handle-b,handle-c, fetches one column per product from
   the alternate product template, and assembles them into a table.

   WHY FETCH RATHER THAN RENDER IN LIQUID. Liquid cannot read the query string
   outside the search template, and /products/{handle}.js does not return
   metafields - and every row here is a metafield. Requesting the product URL
   with ?view=compare-column renders sections/sg-compare-column.liquid with the
   product in scope, giving full metafield access in the correct locale.

   ROW ALIGNMENT IS ON data-row-id, WHICH IS THE LIQUID block.id. Every column
   comes from the same section in the same template, so the ids and their order
   are identical across columns. Do NOT align on data-row-key: two rows may
   legitimately point at the same metafield with different settings.

   SUPPRESSION HAPPENS HERE, NOT IN THE COLUMN. The column emits every row,
   including empty ones, flagged data-empty. Whether a row is worth showing
   depends on ALL the products being compared, which a single column cannot know.

   NO innerHTML. NOT ANYWHERE, NOT EVEN TO CLEAR A CONTAINER. Every value in this
   table originates in merchant-editable data - metafields, metaobjects, product
   titles - and is inserted as text. Two rows nevertheless need markup: the price,
   which is a net and a gross line with VAT labels, and any row carrying a
   certification icon. They are NOT an exception to the rule. The column ships the
   parts as data- attributes on the cell (data-price-net, data-price-gross,
   data-icon) and fillCell() below assembles real elements from them. If a future
   change appears to need innerHTML, the answer is another attribute and another
   createElement, not a relaxation.
   ============================================================================= */

(function () {
  'use strict';

  /* A handle from the query string is attacker-controlled and is interpolated
     into a fetch URL. Validate it rather than trusting it. Anything with a
     slash, a dot or a colon is rejected outright by this pattern. */
  var HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,199}$/;

  function parseHandles(max) {
    var raw = new URLSearchParams(window.location.search).get('items') || '';
    var out = [];
    raw.split(',').forEach(function (h) {
      h = h.trim().toLowerCase();
      if (!HANDLE_RE.test(h)) return;
      if (out.indexOf(h) !== -1) return;
      if (out.length >= max) return;
      out.push(h);
    });
    return out;
  }

  /* Returns the column element, or null if the product could not be fetched.
     A failed column must not blank the whole table - it renders as an
     unavailable cell instead. */
  function fetchColumn(root, handle) {
    return fetch(root + 'products/' + encodeURIComponent(handle) + '?view=compare-column', {
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('[data-sg-compare-column]');
      })
      .catch(function () {
        return null;
      });
  }

  function groupLabels(scope) {
    var map = {};
    scope.querySelectorAll('[data-group-key]').forEach(function (el) {
      map[el.getAttribute('data-group-key')] = el.textContent.trim();
    });
    return map;
  }

  /* Build the row skeleton from the first column that actually arrived. Using
     the first ARRIVED column rather than the first requested one matters: if
     product 1 fails, the table must still be built from products 2 and 3. */
  function rowSkeleton(columns) {
    for (var i = 0; i < columns.length; i++) {
      if (!columns[i]) continue;
      return Array.prototype.map.call(
        columns[i].querySelectorAll('[data-sg-compare-cell]'),
        function (cell) {
          return {
            id: cell.getAttribute('data-row-id'),
            label: cell.getAttribute('data-label') || '',
            group: cell.getAttribute('data-group') || ''
          };
        }
      );
    }
    return [];
  }

  /* An icon URL comes from a merchant-authored metaobject, but it is still
     assigned to img.src, so it is validated rather than trusted. Anything that is
     not an absolute https (or protocol-relative) URL is dropped - which excludes
     javascript:, data: and vbscript: outright. */
  var ICON_RE = /^(?:https:)?\/\/[^\s"'<>]+$/;

  /* WHY THIS IS AN OBJECT AND NOT A STRING.
     Two rows carry more than text: the price cell holds a net and a gross figure
     with their own VAT labels, and any row whose value matches a Product Icons
     entry holds an icon URL. Both need MARKUP in the table, and this file inserts
     values with textContent and never innerHTML. So the column ships the parts as
     attributes and the cell builder below assembles elements from them. Widening
     the descriptor is what keeps that possible without relaxing the rule. */
  function describe(cell) {
    if (!cell) {
      return { type: '', text: '', icon: '', net: '', gross: '', netLabel: '', grossLabel: '' };
    }
    return {
      type: cell.getAttribute('data-cell-type') || '',
      text: cell.textContent.trim(),
      icon: cell.getAttribute('data-icon') || '',
      net: cell.getAttribute('data-price-net') || '',
      gross: cell.getAttribute('data-price-gross') || '',
      netLabel: cell.getAttribute('data-price-net-label') || '',
      grossLabel: cell.getAttribute('data-price-gross-label') || ''
    };
  }

  function valuesFor(columns, rowId) {
    return columns.map(function (col) {
      if (!col) return null;
      return describe(col.querySelector('[data-row-id="' + rowId + '"]'));
    });
  }

  /* null means the COLUMN failed to load; empty means the column loaded and the
     product has no value for this row. Both suppress, but only the first renders
     as data-unavailable. */
  function isBlank(v) {
    if (v === null) return true;
    return v.type === 'price' ? v.gross === '' : v.text === '';
  }

  /* The comparison key for the identical-row rule. It must cover everything the
     cell RENDERS, or two visibly different cells compare equal: two products can
     share a certification value while only one has a mapped icon, and a price is
     two figures rather than one. \u0000 cannot occur in any of these fields. */
  function keyOf(v) {
    if (v.type === 'price') return v.net + '\u0000' + v.gross;
    return v.text + '\u0000' + v.icon;
  }

  function priceLine(className, amount, label) {
    var line = document.createElement('span');
    line.className = className;

    var figure = document.createElement('span');
    figure.className = 'sg-compare__price-amount';
    figure.textContent = amount;
    line.appendChild(figure);

    if (label) {
      var tag = document.createElement('span');
      tag.className = 'sg-compare__price-label';
      tag.textContent = label;
      line.appendChild(tag);
    }
    return line;
  }

  /* EVERY path here is createElement + textContent. Nothing in this function may
     ever be rewritten to assign a string of HTML. */
  function fillCell(td, v) {
    if (v === null) {
      td.textContent = '\u2014';
      td.setAttribute('data-unavailable', 'true');
      return;
    }

    if (v.type === 'price') {
      if (v.gross === '') {
        td.textContent = '\u2014';
        return;
      }
      td.classList.add('sg-compare__value--price');
      /* net is empty on a tax-exclusive market and on volume pricing, where gross
         carries the whole range. One line then, not two. */
      if (v.net !== '') {
        td.appendChild(priceLine('sg-compare__price-net', v.net, v.netLabel));
      }
      td.appendChild(priceLine('sg-compare__price-gross', v.gross, v.grossLabel));
      return;
    }

    if (v.text === '') {
      td.textContent = '\u2014';
      return;
    }

    if (v.icon && ICON_RE.test(v.icon)) {
      var img = document.createElement('img');
      img.className = 'sg-compare__icon';
      img.src = v.icon;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      img.setAttribute('aria-hidden', 'true');
      td.appendChild(img);
      td.classList.add('sg-compare__value--badge');
    }

    var span = document.createElement('span');
    span.className = 'sg-compare__value-text';
    span.textContent = v.text;
    td.appendChild(span);
  }

  function render(mount, columns, labels, removeLabel) {
    var rows = rowSkeleton(columns);
    var table = document.createElement('table');
    table.className = 'sg-compare__table';

    /* Header: one cell per product, carrying the image and title lifted from
       the fetched column. */
    var thead = document.createElement('thead');
    var htr = document.createElement('tr');
    htr.appendChild(document.createElement('th'));
    columns.forEach(function (col) {
      var th = document.createElement('th');
      th.className = 'sg-compare__product';
      if (col) {
        var header = col.querySelector('[data-sg-compare-header]');
        if (header) th.appendChild(header.cloneNode(true));
      } else {
        th.textContent = removeLabel ? '' : '';
        th.classList.add('sg-compare__product--missing');
      }
      htr.appendChild(th);
    });
    thead.appendChild(htr);
    table.appendChild(thead);

    var tbody = document.createElement('tbody');
    var lastGroup = null;
    var shown = 0;

    rows.forEach(function (row) {
      var values = valuesFor(columns, row.id);

      /* RULE 1: hide a row when no product has a value for it. This is what
         lets one 36-row set serve every product type without filling the
         table with em-dashes. */
      var anyValue = values.some(function (v) {
        return !isBlank(v);
      });
      if (!anyValue) return;

      if (row.group && row.group !== lastGroup) {
        var gtr = document.createElement('tr');
        gtr.className = 'sg-compare__group-row';
        var gth = document.createElement('th');
        gth.setAttribute('colspan', String(columns.length + 1));
        gth.scope = 'colgroup';
        gth.textContent = labels[row.group] || row.group;
        gtr.appendChild(gth);
        tbody.appendChild(gtr);
        lastGroup = row.group;
      }

      /* RULE 2: dim rows where every product says the same thing. The value of
         a comparison is the differences; identical rows are noise. Only marked,
         never removed - the differences-only toggle hides them on request. */
      var present = values.filter(function (v) {
        return !isBlank(v);
      });
      var identical = present.length === columns.length &&
        present.every(function (v) {
          return keyOf(v) === keyOf(present[0]);
        });

      var tr = document.createElement('tr');
      tr.className = 'sg-compare__row';
      if (identical) tr.setAttribute('data-identical', 'true');

      var thLabel = document.createElement('th');
      thLabel.scope = 'row';
      thLabel.className = 'sg-compare__label';
      thLabel.textContent = row.label;
      tr.appendChild(thLabel);

      values.forEach(function (v) {
        var td = document.createElement('td');
        td.className = 'sg-compare__value';
        /* textContent, never innerHTML. Values originate in merchant data and are
           inserted as text so no markup can execute. Where a cell needs structure
           it is BUILT, element by element - see fillCell. */
        fillCell(td, v);
        tr.appendChild(td);
      });

      tbody.appendChild(tr);
      shown += 1;
    });

    table.appendChild(tbody);
    /* textContent, not innerHTML. Both empty the mount identically. The previous
       version cleared with innerHTML, which meant the file's only ASSIGNMENT to
       innerHTML was a legitimate one - so any gate had to carve out an exception
       for it, and an exception is where the next real one hides. There is now no
       assignment to carve out.

       A GREP IS STILL NOT A GATE. The word survives in this comment and five
       others above, so a grep for "innerHTML" reports six hits on a correct file,
       exactly as it once fired on a comment and was believed. The gate must parse
       for an assignment or a call, not match the string. */
    mount.textContent = '';
    mount.appendChild(table);
    return shown;
  }

  function init() {
    var root = document.querySelector('[data-sg-compare]');
    if (!root) return;

    var mount = root.querySelector('[data-sg-compare-mount]');
    var empty = root.querySelector('[data-sg-compare-empty]');
    var controls = root.querySelector('[data-sg-compare-controls]');
    /* routes.root_url returns '/' on the primary locale but '/de' - with NO
       trailing slash - on a localised one. Concatenating naively produces
       '/deproducts/...', which 404s, so every column comes back null and the
       page silently shows its empty state. Normalise before use. */
    var base = root.getAttribute('data-root') || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    var max = parseInt(root.getAttribute('data-max'), 10) || 3;
    var labels = groupLabels(root);

    var script = document.querySelector('script[data-remove-label]');
    var removeLabel = script ? script.getAttribute('data-remove-label') : '';

    var handles = parseHandles(max);
    if (!handles.length) return; // empty state is already in the markup

    Promise.all(
      handles.map(function (h) {
        return fetchColumn(base, h);
      })
    ).then(function (columns) {
      if (!columns.some(Boolean)) return; // nothing resolved; leave empty state
      if (empty) empty.hidden = true;
      var shown = render(mount, columns, labels, removeLabel);
      if (controls && shown) controls.hidden = false;
    });

    if (controls) {
      controls.addEventListener('change', function (event) {
        if (!event.target.matches('[data-sg-compare-diff-only]')) return;
        root.toggleAttribute('data-diff-only', event.target.checked);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
